import React from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FilterChip } from '@drone-js/core';
import { ActivityGqlOperation, OperationKey, SearchRequestType } from 'store/api/api.types';
import { mapDispatchToProps, mapStateToProps, SearchPageProps } from 'component/page/search/Search/SearchPage.selectors';
import { getPageNumberFromQueryParams } from 'component/page/search/Pagination/pagination.utils';
import { StickyTopBar } from 'component/common/StickyTopBar/StickyTopBar';
import { PageContentStatus } from 'core/type/PageContentStatus';
import { ActivitiesFooter } from 'component/common/Footer/ActivitiesFooter';
import { withErrorBoundary } from 'component/common/Hoc/ErrorBoundary';
import { trackPageVisitEvent } from 'core/messaging/analytics/analytics';
import SearchBar from 'component/common/SearchBar/SearchBar';
import SearchSupplyError from 'component/page/search/Error/SearchSupplyError';
import SearchPageContent from 'component/page/search/SearchPageContent/SearchPageContent';
import TYPRedirectBanner from 'component/common/TYPRedirectBanner/TYPRedirectBanner';
import { SearchPageContextFields } from 'core/messaging/analytics/pages/pageEventsMapping';
import SortAndFilterOverlay from 'component/page/search/SortAndFilter/SortAndFilterOverlay/SortAndFilterOverlay';
import CategoriesTabs from 'component/page/search/Categories/CategoriesTabs';
import PageContainer from 'component/common/PageContainer/PageContainer';
import { DefaultRequestProps } from 'store/api/ApiConstants';
import queryActivitiesGqlApi from 'store/api/activitiesGqlApi';
import { OrchestrateTopActivitiesInSessionStorage } from 'core-framework/util/EmptyStateSuggestion';
import { getCityIdQueryParams, getKeywordQueryParams } from 'gql/service/graph/activityService';
import { FeatureName } from 'core/constant/FeatureName';
import Feature from 'component/common/Experiment/Feature';
import { isOn } from 'core/window/bootstrapConfig';
import { CartFloatingContainer } from 'component/common/CartFloatingButtonContainer/CartFloatingButtonContainer';
import StandardErrorPage from 'component/page/error/ActivityStandardErrorPage';
import { SearchType } from 'gql/autogen/SearchType';
import CategoryBottomSheet from 'component/page/search/SortAndFilter/CategoryBottomSheet/CategoryBottomSheet';
import { SortCode } from 'gql/autogen/SortCode';
import { SortOrder } from 'gql/autogen/SortOrder';

interface SearchPageContentWrapperProps {
    contentStatus: PageContentStatus;
    isNoResults: boolean;
    isGlobalKeywordSearch?: boolean;
}

const SearchPageContentWrapper: React.FC<SearchPageContentWrapperProps> = React.memo((props) => {
    if (!props.isGlobalKeywordSearch && props.contentStatus === PageContentStatus.StandardError) {
        return <StandardErrorPage page="search" contentStatus={props.contentStatus} />;
    }

    if (props.contentStatus === PageContentStatus.SupplyError) {
        return (
            <PageContainer data-testid="activities-search-page" display="flex" alignItems="flex-start" overflowY="visible">
                <SearchSupplyError />
            </PageContainer>
        );
    }

    return <SearchPageContent isNoResults={props.isNoResults} searchPagePadding={true} isKeywordDisplayed={true} />;
});
SearchPageContentWrapper.displayName = 'SearchPageContentWrapper';

export const SearchPageComponent: React.FC<SearchPageProps> = (props) => {
    const location = useLocation();
    const locationSearch = location.search;
    const keyword = getKeywordQueryParams(locationSearch);
    const {
        isLoading,
        isSearchBoxFeatureGroup,
        contentStatus,
        config,
        filteredResultNumber,
        onLocationSearchChange,
        closeAllOverlays,
        onClickBackOverlay,
        filterCount,
        onSortAndFilterButtonClick,
        isSortAndFilterOverlayShow,
        isNotMobile,
        screenSize,
        isFixOverlayIssue,
        setTextSearchState,
    } = props;
    const isNoResults = filteredResultNumber === 0 && !isLoading;
    const cms = config.cms;
    const paginationSize = DefaultRequestProps.search.pagination.size;
    const isSubcategoryFilterEnabled = isOn(FeatureName.SubcategoryFilter);
    const cityId = getCityIdQueryParams(location.search);
    const isSearchPage = location.pathname.includes('/search');
    const isGlobalKeywordSearch = props.isGlobalKeywordSearchEnabled && isSearchPage && !cityId;

    React.useEffect(() => {
        isGlobalKeywordSearch && setTextSearchState(keyword || '');
    }, [isGlobalKeywordSearch, setTextSearchState, keyword]);

    React.useEffect(() => {
        if (isSearchBoxFeatureGroup) {
            OrchestrateTopActivitiesInSessionStorage(config);
        }
    }, [config, isSearchBoxFeatureGroup]);

    React.useEffect(() => {
        if (contentStatus === PageContentStatus.Success) {
            trackPageVisitEvent<SearchPageContextFields>({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentStatus]);

    React.useEffect(() => {
        const pageNumber = getPageNumberFromQueryParams(locationSearch);
        const searchType = isGlobalKeywordSearch ? SearchType.TEXT : SearchType.CITY;
        const searchRequestType = isGlobalKeywordSearch ? SearchRequestType.GLOBAL_SEARCH : SearchRequestType.IN_CITY_SEARCH;
        // onLocationSearchChange need to be called before queryActivitiesGqlApi because there is setSearchStateFromQueryParams inside.
        onLocationSearchChange(locationSearch, pageNumber);
        queryActivitiesGqlApi({
            args: {
                props: {
                    searchType,
                    searchRequestType,
                    pagination: { number: pageNumber, size: paginationSize },
                    keyword,
                    defaultSort: isGlobalKeywordSearch ? { code: SortCode.Default, order: SortOrder.Default } : { code: SortCode.ActivitiesSsr, order: SortOrder.Descending },
                },
            },
            operation: ActivityGqlOperation.search,
            key: isGlobalKeywordSearch ? OperationKey.GlobalKeywordSearch : OperationKey.Search,
        });
        closeAllOverlays();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationSearch, paginationSize, keyword, isGlobalKeywordSearch]);

    const trailingAssets = screenSize.desktop ? undefined : (
        <FilterChip
            label={cms[275607]}
            leadingIcon="fill.symbol.filter2"
            counter={filterCount}
            aria-label={cms[285482]}
            state={isLoading ? 'disabled' : 'enabled'}
            data-element-name="activities-sort-and-filter-open-button"
            data-testid="activities-sort-and-filter-open-button"
            dropdown={false}
            size="m"
            ms="16"
            onClick={onSortAndFilterButtonClick}
        />
    );

    return (
        <>
            <TYPRedirectBanner />
            <StickyTopBar
                elementName="activities-header-search-bar"
                zIndex={isFixOverlayIssue ? 4 : undefined}
                contentAreaProps={{ px: '8', py: '8', display: 'flex', alignItems: 'center' }}
                noShadow={!isSubcategoryFilterEnabled}
            >
                <SearchBar trailingAsset={trailingAssets} />
            </StickyTopBar>
            <Feature.Off name={FeatureName.SubcategoryFilter}>
                <StickyTopBar
                    elementName="activities-categories-container"
                    elementNameToCalcHeight="activities-header-search-bar"
                    targetAttribute="top"
                    fullWidth={true}
                    zIndex={2}
                >
                    <CategoriesTabs isLoading={isLoading} isNoResults={isNoResults} />
                </StickyTopBar>
            </Feature.Off>
            <SearchPageContentWrapper contentStatus={contentStatus} isNoResults={isNoResults} isGlobalKeywordSearch={isGlobalKeywordSearch} />
            <SortAndFilterOverlay visible={isSortAndFilterOverlayShow} onClose={closeAllOverlays} onClickBack={onClickBackOverlay} cms={cms} />
            {isNotMobile && <CartFloatingContainer />}
            <Feature.On name={FeatureName.SubcategoryFilter}>
                <CategoryBottomSheet cms={cms} onClose={closeAllOverlays} />
            </Feature.On>
            <ActivitiesFooter />
        </>
    );
};
SearchPageComponent.displayName = 'SearchPageComponent';

const SearchPage = withErrorBoundary(SearchPageComponent);
SearchPage.displayName = 'SearchPage';

export default connect(mapStateToProps, mapDispatchToProps)(SearchPage);
